import type { MouseEventHandler, RefObject } from 'react';
import { useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';

import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useBoardId } from '@trello/id-context';
import {
  useIsBoardPanelOpen,
  useIsPlannerPanelOpen,
} from '@trello/split-screen';

import { bulkActionSelectedCardsSharedState } from 'app/src/components/BulkAction/bulkActionSelectedCardsSharedState';
import { useIsBulkActionEnabled } from 'app/src/components/BulkAction/useIsBulkActionEnabled';
import type { CardType } from 'app/src/components/CardType';
import { useIsInboxBoard } from 'app/src/components/Inbox';
import { InboxModalContext } from 'app/src/components/Inbox/InboxModalContext';
import { inboxSelectedCardsSharedState } from 'app/src/components/Inbox/inboxSelectedCardsSharedState';

/**
 * Card types that can be opened via click or Enter keypress.
 * In the future, I think this can support all card types; right now,
 * however, board & link cards are rendered in both stacks, so it's not
 * trivial to refactor them. Once the old stack has been cleaned up, I
 * think we could expand this to support click handling for all types.
 */
export const CARD_TYPES_WITH_CARD_BACK = new Set<CardType>([
  'mirror',
  'default',
  'cover',
]);

interface UseCardFrontClickHandlerOptions {
  cardId: string;
  cardFrontRef: RefObject<HTMLElement>;
  cardType: CardType;
  cardUrl?: string;
  openCardBackDialog: (cardId: string) => Promise<void>;
}

/**
 * Handles click events on the card front container.
 * While an anchor tag is configured as the card name for accessibility,
 * we actually want the entire card front element to be clickable.
 */
export const useCardFrontClickHandler = ({
  cardId,
  cardFrontRef,
  cardType,
  cardUrl,
  openCardBackDialog,
}: UseCardFrontClickHandlerOptions) => {
  const boardId = useBoardId();
  const isInboxBoard = useIsInboxBoard();
  const { value: isInboxBulkActionEnabled } = useFeatureGate(
    'trello_web_inbox_bulk_actions',
  );
  const isBulkActionEnabled = useIsBulkActionEnabled();
  const { value: isSplitScreenEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );
  const isBoardPanelOpen = useIsBoardPanelOpen();
  const isPlannerPanelOpen = useIsPlannerPanelOpen();
  const { modalId: isShowingModal, hideModal } = useContextSelector(
    InboxModalContext,
    (context) => context,
  );

  return useCallback<MouseEventHandler>(
    (e) => {
      // Global bulk action
      if (e.shiftKey && isBulkActionEnabled) {
        e.stopPropagation();
        e.preventDefault();

        const currentState = bulkActionSelectedCardsSharedState.value;

        const boardCards = { ...currentState[boardId] };

        if (boardCards[cardId]) {
          delete boardCards[cardId];
        } else {
          boardCards[cardId] = true;
        }

        const newState = { ...currentState };

        if (Object.keys(boardCards).length === 0) {
          delete newState[boardId];
        } else {
          newState[boardId] = boardCards;
        }

        bulkActionSelectedCardsSharedState.setValue(() => newState);
        return;
      }

      if (e.shiftKey && isInboxBulkActionEnabled && isInboxBoard) {
        e.stopPropagation();
        e.preventDefault();
        const newSet = new Set(inboxSelectedCardsSharedState.value);

        if (newSet.has(cardId)) {
          newSet.delete(cardId);
        } else {
          newSet.add(cardId);
        }
        inboxSelectedCardsSharedState.setValue(newSet);
        return;
      }

      // Functionally just disables this callback in Storybook.
      if (!cardUrl) return;
      if (!CARD_TYPES_WITH_CARD_BACK.has(cardType)) return;
      if (!isInboxBoard && cardType === 'mirror') {
        return;
      }
      if (e.ctrlKey || e.shiftKey) {
        Analytics.sendClickedButtonEvent({
          buttonName: 'cardFrontNewTab',
          source: 'boardScreen',
          attributes: {
            key: e.ctrlKey ? 'ctrl' : 'shift',
          },
        });
        return;
      }
      // Ignore clicks on elements outside of the card front, e.g. popovers.
      if (!cardFrontRef.current?.contains(e.target as HTMLElement)) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      // Don't open card back of non-inbox or non-planner cards if board panel is closed
      if (
        isSplitScreenEnabled &&
        !isBoardPanelOpen &&
        !isInboxBoard &&
        !isPlannerPanelOpen
      ) {
        return;
      }

      if (isShowingModal) {
        hideModal();
      }

      if (e.metaKey) {
        window.open(cardUrl, '_blank');
        return;
      }

      openCardBackDialog(cardId);
    },
    [
      isBulkActionEnabled,
      isInboxBulkActionEnabled,
      isInboxBoard,
      cardUrl,
      cardType,
      cardFrontRef,
      isSplitScreenEnabled,
      isBoardPanelOpen,
      isPlannerPanelOpen,
      isShowingModal,
      openCardBackDialog,
      cardId,
      boardId,
      hideModal,
    ],
  );
};
